import React, { startTransition, useEffect, useMemo, useState } from "react";
import { graphql, HeadProps, PageProps } from "gatsby";
import {
  BrochureFragment,
  RequestBrochurePageGroupQuery,
  SitePageContext
} from "types/graphql-types";

import { SectionContainer } from "@silversea-ssc/ui-components-react";
import PageBuilderOpeningText from "components/PageBuilder/PageBuilderOpeningText";
import * as styles from "./RequestBrochureGroupPage.module.scss";

import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { getActiveLanguages, getDefaultLanguage } from "./utils";
import { isPresent } from "src/utils/checks";
import {
  BrochureLanguageCode,
  brochuresForLanguage,
  filterBrochuresByLocation
} from "src/utils/brochures";
import { PageContext } from "types/pageContext";
import GatsbyHeadOpenGraph from "components/SEO/GatsbyHeadOpenGraph";
import BrochureCard from "src/templates/RequestBrochure/BrochureCard";
import {
  BrochureFilters,
  BrochureGroupFilterItem,
  MobileBrochureFilters
} from "src/templates/RequestBrochure/BrochureFilters";
import { t } from "@lingui/core/macro";
import { useBrochureUrlFilter } from "src/templates/RequestBrochure/useBrochureUrlFilters";
const NewRequestBrochureModal = React.lazy(
  () => import("components/RequestBrochureModal/RequestBrochureModal")
);

type RequestBrochurePageContext = SitePageContext & {
  brochureGroupName: string;
};

export const allBrochureContentGroupId = "all";

const RequestBrochurePageGroup: React.FC<
  React.PropsWithChildren<
    PageProps<RequestBrochurePageGroupQuery, RequestBrochurePageContext>
  >
> = ({ data, pageContext, ...props }) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const page = data.page!;
  const thankYouPageURL = data.thankYouPageURL?.path;

  const [selectedFilter, onBrochureFilterChange] = useBrochureUrlFilter();

  const brochures = (data.brochures?.brochures || []).filter(isPresent);
  const language = useLanguage();
  const geolocation = useGeolocation();
  const [modalOpened, setModalOpened] = useState<
    | {
        brochure: BrochureFragment;
      }
    | undefined
  >(undefined);

  const filteredBrochures = React.useMemo(() => {
    if (!isPresent(brochures)) return [];
    return filterBrochuresByLocation(brochures, geolocation);
  }, [brochures, geolocation]);

  const languageGroups = getActiveLanguages(filteredBrochures);
  const defaultLanguage = getDefaultLanguage(languageGroups, language.code);

  const [selectedLanguage, setSelectedLanguage] =
    useState<BrochureLanguageCode>(defaultLanguage);

  const brochuresByLanguage = useMemo(
    () =>
      selectedLanguage
        ? brochuresForLanguage(selectedLanguage, filteredBrochures)
        : [],
    [filteredBrochures, selectedLanguage]
  );

  useEffect(() => {
    startTransition(() => {
      setSelectedLanguage(defaultLanguage);
    });
  }, [geolocation.countryCode, defaultLanguage, filteredBrochures.length]);

  const brochuresByLanguageWithGroup = React.useMemo(
    () =>
      brochuresByLanguage
        .map((brochure) => {
          const { _id } = brochure.brochureContentGroup || {};
          const groupContent = data.brochureContentGroups.nodes.find(
            (c) => c._id == _id
          );
          if (!groupContent || !_id) return undefined;

          return {
            ...brochure,
            groupId: groupContent._id,
            groupTitle: groupContent.title?.localized,
            orderRank: groupContent.orderRank!
          };
        })
        .filter(isPresent)
        .sort((a, b) => a.orderRank.localeCompare(b.orderRank)),
    [data.brochureContentGroups.nodes, brochuresByLanguage]
  );

  const groupFilteredBrochures = useMemo(() => {
    if (selectedFilter == "all") return brochuresByLanguageWithGroup;
    return brochuresByLanguageWithGroup.filter(
      (b) => b.groupId == selectedFilter
    );
  }, [brochuresByLanguageWithGroup, selectedFilter]);

  const brochureGroupsFilters = useMemo(() => {
    return brochuresByLanguageWithGroup.reduce(
      (acc, brochure) => {
        const currentGroup = acc.find((x) => x.id == brochure.groupId);
        if (currentGroup) {
          currentGroup.count += 1;
        } else {
          acc.push({
            id: brochure.groupId!,
            title: brochure.groupTitle!,
            count: 1
          });
        }

        return acc;
      },
      [
        {
          id: allBrochureContentGroupId,
          title: t`All brochures`,
          count: brochuresByLanguageWithGroup.length
        }
      ] as BrochureGroupFilterItem[]
    );
  }, [brochuresByLanguageWithGroup]);

  return (
    <React.Fragment>
      <SectionContainer
        spacingTop="regular"
        spacingBottom="compact"
        backgroundColor="neutral01"
      >
        <PageBuilderOpeningText {...page.heading} />
      </SectionContainer>
      <MobileBrochureFilters
        onFilterChange={onBrochureFilterChange}
        selectedFilter={selectedFilter}
        brochureGroups={brochureGroupsFilters}
      />
      <BrochureFilters
        onFilterChange={onBrochureFilterChange}
        selectedFilter={selectedFilter}
        brochureGroups={brochureGroupsFilters}
      />
      <SectionContainer
        spacingTop="regular"
        spacingBottom="regular"
        backgroundColor="neutral01"
        dataContentName="brochures"
        className={styles.brochureContentWrapper}
      >
        <div className={styles.brochureGroupResults}>
          {groupFilteredBrochures.map((b) => (
            <BrochureCard
              key={b._id}
              brochure={b}
              groupTitle={b.groupTitle}
              onClick={() =>
                setModalOpened({
                  brochure: b
                })
              }
            />
          ))}
        </div>
      </SectionContainer>
      {modalOpened && thankYouPageURL && (
        <React.Suspense>
          <NewRequestBrochureModal
            isOpen={!!modalOpened}
            onRequestClose={() => setModalOpened(undefined)}
            brochure={modalOpened.brochure}
            thankYouPageURL={thankYouPageURL}
            formDisclaimerContent={data.formDisclaimerContent}
            thankYouStragegy={"redirect"}
          />
        </React.Suspense>
      )}
    </React.Fragment>
  );
};

export const Head = (
  props: HeadProps<RequestBrochurePageGroupQuery, PageContext>
) => {
  const { data, pageContext } = props;
  return (
    <GatsbyHeadOpenGraph
      path={props.location.pathname}
      alternateLinks={pageContext.alternateLinks}
      languageCode={pageContext.languageCode}
      openGraph={data.page?.metadata?.openGraph}
      excludeFromSitemap={true}
    />
  );
};

export const query = graphql`
  fragment BrochureWithContentGroupRef on SanityBrochure {
    ...Brochure
    brochureContentGroup {
      _id
    }
  }

  query RequestBrochurePageGroup(
    $parentPageId: String!
    $brochureGroupName: String!
    $languageCode: String
  ) {
    page: sanityRequestBrochure(id: { eq: $parentPageId }) {
      metadata {
        openGraph {
          ...OpenGraphMetaTags
        }
      }
      heading {
        ...PageBuilderOpeningText
      }
    }
    brochures: sanityBrochureGrouping(
      brochures: { elemMatch: { isActive: { eq: true } } }
      name: { eq: $brochureGroupName }
    ) {
      brochures {
        ...BrochureWithContentGroupRef
      }
    }
    brochureContentGroups: allSanityBrochureContentGroup {
      nodes {
        _id
        orderRank
        title {
          localized
        }
        content {
          localized
        }
      }
    }
    thankYouPageURL: sitePage(
      context: {
        sanityId: { regex: "/request-brochure-thankyou-page$/" }
        languageCode: { eq: $languageCode }
        pageType: { eq: PAGE }
      }
    ) {
      path
    }
    formDisclaimerContent: sanityFormDisclaimer {
      ...FormDisclaimer
    }
  }
`;

export default RequestBrochurePageGroup;
